﻿// src/modules/recording.js — Recording, pause/resume, auto-save, history, timer, wake lock
import { state } from '../state.js';
import { sendBeacon, UNSENT_SESSION_STATUSES, MAX_DISPLAY_HOURS } from '../utils/constants.js';
import { apiFetch } from '../utils/api.js';
import { escapeHtml, showView, showConfirmDialog, showToast, setStatus } from '../utils/dom.js';
import { formatTimeAgo, pad, formatDuration, monotonicNow } from '../utils/format.js';
import { RecordingStore, SessionStore } from '../utils/storage.js';
import { ProfileStore } from '../stores/profile-store.js';
import { logger, apiFetchWithRefresh, addUploadProgressLog } from './debug.js';
import { hideLiveTranscriptUI } from './feedback.js';
import { uploadAndProcess, checkDuplicateTag, confirmDuplicateTag } from './upload.js';
import { attachPatientPicker, fetchPatientSummary, historyLabelSuggestions } from './patients.js';
import { getAllLoadedNotes, loadNotes } from './notes.js';

// B1: unified Record Patient control. The picker drives off the free-text
// #patientLabel input (one control, not two). Holds a patientId only when the
// doctor explicitly selects/creates a patient; null otherwise so the create flow
// stays EXACTLY as before (no patientId sent).
let recordPatientPicker = null;
// FEAT-001: guards an in-flight summary fetch against out-of-order responses /
// a selection change mid-request.
let summaryReqSeq = 0;

export function initRecordPatientPicker() {
    const host = document.getElementById('recordPatientPicker');
    if (!host || recordPatientPicker) return;
    // B1: the free-text #patientLabel IS the picker's search field, so the doctor
    // sees ONE Patient control. The dropdown merges roster matches + label-history
    // (distinct patientLabels from already-loaded notes) + "New patient".
    const labelInput = document.getElementById('patientLabel');
    recordPatientPicker = attachPatientPicker(host, {
        onChange: onRecordPatientChange,
        searchInput: labelInput,
        extraSuggestions: (query, rosterLabels) =>
            historyLabelSuggestions(getAllLoadedNotes(), query, rosterLabels),
    });
    // Label-history suggestions need the notes cache; load it if the Record tab
    // opens before Notes was ever visited (a read already done elsewhere, not new).
    if (!getAllLoadedNotes().length) loadNotes().catch(() => {});
    const btn = document.getElementById('previsitSummaryBtn');
    if (btn) btn.addEventListener('click', onPrevisitSummaryClick);
}

function onRecordPatientChange(patientId) {
    const btn = document.getElementById('previsitSummaryBtn');
    if (btn) btn.classList.toggle('d-none', !patientId);
    resetPrevisitSummaryPanel();
    // B2: on selecting a known patient, show their previous notes inline
    // immediately (notesOnly=1 — no Bedrock cost). The #previsitSummaryBtn still
    // generates the full AI summary on demand.
    if (patientId) loadPrevisitSummary(patientId, true);
}

/** The selected patientId for a new recording, or null when nothing is selected. */
export function getRecordPatientId() {
    return recordPatientPicker ? recordPatientPicker.getPatientId() : null;
}

/** Clear the record picker selection (full state reset on view change / after completion). */
export function resetRecordPatientPicker() {
    if (recordPatientPicker) recordPatientPicker.reset();
    resetPrevisitSummaryPanel();
    const btn = document.getElementById('previsitSummaryBtn');
    if (btn) btn.classList.add('d-none');
}

// ============================================================================
// FEAT-001: Pre-visit summary — on-demand panel (sourceNotes first, then summary).
// ============================================================================
function resetPrevisitSummaryPanel() {
    summaryReqSeq++; // abandon any in-flight fetch
    const panel = document.getElementById('previsitSummaryPanel');
    if (!panel) return;
    panel.innerHTML = '';
    panel.classList.add('d-none');
}

async function onPrevisitSummaryClick() {
    const patientId = getRecordPatientId();
    if (!patientId) return;
    await loadPrevisitSummary(patientId); // full AI summary (no flag, cached)
}

async function loadPrevisitSummary(patientId, notesOnly) {
    const panel = document.getElementById('previsitSummaryPanel');
    if (!panel) return;
    const seq = ++summaryReqSeq;
    panel.classList.remove('d-none');
    panel.innerHTML =
        '<div class="card"><div class="card-body text-center py-3 text-muted">' +
        '<span class="spinner-border spinner-border-sm" role="status"></span> ' +
        (notesOnly ? 'Loading previous notes...' : 'Loading pre-visit summary...') +
        '</div></div>';
    try {
        const data = await fetchPatientSummary(patientId, notesOnly);
        if (seq !== summaryReqSeq) return; // selection changed / superseded
        renderPrevisitSummary(panel, data, notesOnly);
    } catch (err) {
        if (seq !== summaryReqSeq) return;
        renderPrevisitSummaryError(panel, patientId);
    }
}

function renderPrevisitSummaryError(panel, patientId) {
    panel.innerHTML =
        '<div class="alert alert-danger d-flex align-items-center justify-content-between mb-0" role="alert">' +
        '<span>Could not load summary.</span>' +
        '<button type="button" class="btn btn-sm btn-outline-danger ms-2" data-summary-retry style="min-height:44px">Retry</button>' +
        '</div>';
    const retry = panel.querySelector('[data-summary-retry]');
    if (retry) retry.addEventListener('click', () => loadPrevisitSummary(patientId));
}

function renderPrevisitSummary(panel, data, notesOnly) {
    const sourceNotes = Array.isArray(data.sourceNotes) ? data.sourceNotes : [];

    // sourceNotes list FIRST (safety: doctor verifies the link before trusting the summary).
    // Height-capped + scroll-contained so the panel stays within the iPad ~550px budget.
    let notesHtml = '';
    if (sourceNotes.length) {
        let rows = '';
        for (const n of sourceNotes) {
            const date = (n.createdAt || '').slice(0, 10);
            const shortId = (n.noteId || '').slice(0, 8);
            rows +=
                '<div class="list-group-item d-flex justify-content-between align-items-center py-2">' +
                '<span class="text-truncate me-2">' + escapeHtml(n.patientLabel || '') + '</span>' +
                '<span class="text-muted small text-nowrap">' + escapeHtml(date) +
                ' &middot; ' + escapeHtml(shortId) + '</span>' +
                '</div>';
        }
        // Header wording differs: the notesOnly preview is a "previous notes" list;
        // the full-summary mode labels it as the summary's sources.
        const header = notesOnly
            ? ('Previous notes (' + sourceNotes.length + ')')
            : ('Summary drawn from ' + sourceNotes.length + ' note' + (sourceNotes.length === 1 ? '' : 's') + ':');
        notesHtml =
            '<div class="card-header py-2 small text-muted">' + header + '</div>' +
            '<div class="list-group list-group-flush overflow-auto" style="max-height:140px">' + rows + '</div>';
    }

    // skippedNotes warning (ids only — no PHI). Not present in notesOnly mode.
    let skippedHtml = '';
    if (Array.isArray(data.skippedNotes) && data.skippedNotes.length) {
        skippedHtml =
            '<div class="card-body py-2 border-top"><p class="text-warning small mb-0">' +
            data.skippedNotes.length + ' note' + (data.skippedNotes.length === 1 ? '' : 's') +
            ' could not be read and were left out.</p></div>';
    }

    // Body.
    let bodyHtml;
    if (notesOnly) {
        // Preview mode: the list IS the content. Empty history → graceful info, not an error.
        bodyHtml = sourceNotes.length
            ? ''
            : '<div class="card-body"><div class="alert alert-info mb-0" role="alert">' +
              'No prior notes linked yet.' +
              '</div></div>';
    } else if (data.summary === null || data.summary === undefined || data.summary === '') {
        bodyHtml =
            '<div class="card-body border-top"><div class="alert alert-info mb-0" role="alert">' +
            'No prior notes linked yet &mdash; nothing to summarize.' +
            '</div></div>';
    } else {
        bodyHtml =
            '<div class="card-body border-top"><p class="mb-0" style="white-space:pre-wrap">' +
            escapeHtml(data.summary) + '</p></div>';
    }

    panel.innerHTML = '<div class="card">' + notesHtml + skippedHtml + bodyHtml + '</div>';
}

// Local helper (also defined in main.js scope — duplicated here to avoid cross-module ReferenceError)
function showInputError(inputEl, errorElId) {
    inputEl.classList.add('input-error');
    const errorEl = document.getElementById(errorElId);
    if (errorEl) errorEl.classList.remove('hidden');
    inputEl.focus();
    const handler = () => {
        inputEl.classList.remove('input-error');
        if (errorEl) errorEl.classList.add('hidden');
        inputEl.removeEventListener('input', handler);
    };
    inputEl.addEventListener('input', handler);
}

// ============================================================================
// Mic permission error UI helpers
// ============================================================================
function showMicPermissionError(errorType) {
    const container = document.getElementById('micPermissionError');
    if (!container) return;
    let title = '';
    let steps = '';
    const isMobileSafari = /iPhone|iPad|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS/.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    if (errorType === 'permission_denied') {
        title = '🎙️ Microphone access was denied';
        if (isMobileSafari) {
            steps = '<ol><li>Go to <strong>Settings → Safari → Microphone</strong></li><li>Set to <strong>Allow</strong></li><li>Return here and reload the page</li></ol>';
        } else if (isAndroid) {
            steps = '<ol><li>Tap the <strong>🔒 lock icon</strong> in the address bar</li><li>Tap <strong>Permissions</strong></li><li>Set <strong>Microphone</strong> to <strong>Allow</strong></li><li>Reload the page and try again</li></ol>';
        } else {
            steps = '<ol><li>Click the <strong>🔒 lock icon</strong> in the address bar</li><li>Find <strong>Microphone</strong> and set to <strong>Allow</strong></li><li>Reload the page and try again</li></ol>';
        }
    } else if (errorType === 'no_microphone') {
        title = '🎙️ No microphone found';
        steps = '<p>Please connect a microphone or headset and try again.</p>';
    } else if (errorType === 'mic_in_use') {
        title = '🎙️ Microphone is in use';
        steps = '<p>Another app may be using the microphone. Close other recording apps and try again.</p>';
    } else if (errorType === 'constraints_unsatisfied') {
        title = '🎙️ Microphone settings not supported';
        steps = '<p>Your device does not support the requested audio settings. Please try reloading the page — this should resolve automatically.</p>';
    } else {
        title = '🎙️ Could not access microphone';
        steps = '<p>Please check your device settings and try again.</p>';
    }
    container.innerHTML = '<div class="mic-error-title">' + title + '</div>' + steps;
    container.classList.remove('hidden');
}

function hideMicPermissionError() {
    const container = document.getElementById('micPermissionError');
    if (container) container.classList.add('hidden');
}

export function elapsedSinceStart() {
    if (state.startTimeMono === null || state.startTimeMono === undefined) return 0;
    const delta = monotonicNow() - state.startTimeMono;
    return delta > 0 ? Math.round(delta) : 0;
}

export function totalElapsedMs() {
    const total = (state.elapsedBeforePause || 0) + elapsedSinceStart();
    return total > 0 ? total : 0;
}

// FIX A3: segment bookkeeping â€” always push blob + meta together.
export function pushRecordingSegment(blob, mimeType, generation) {
    if (!blob || blob.size === 0) return;
    state.recordingSegments.push(blob);
    state.recordingSegmentMeta.push({
        size: blob.size,
        mimeType: mimeType || blob.type || '',
        generation: (typeof generation === 'number') ? generation : state.recorderGeneration,
    });
}

export function resetRecordingSegments() {
    state.recordingSegments = [];
    state.recordingSegmentMeta = [];
    state.lastSegmentJoinInfo = null;
}

// Adopt segments loaded from IndexedDB. Their true generation is unknown, so
// they are treated as one prior generation (-1) tagged with the session mimeType.
export function adoptRecordingSegments(blobs, mimeType) {
    state.recordingSegments = blobs.slice();
    state.recordingSegmentMeta = blobs.map(b => ({
        size: b.size || 0,
        mimeType: mimeType || b.type || '',
        generation: -1,
    }));
    state.lastSegmentJoinInfo = null;
}

export function normalizeMimeType(mime) {
    return (mime || '').split(';')[0].trim().toLowerCase();
}

export async function toggleRecording() {
    if (state.isRecording || state.isPaused) {
        // Recording is active â€” this shouldn't be called, but safety fallback
        return;
    }
    // Validate patient name before starting
    const patientInput = document.getElementById('patientLabel');
    if (!patientInput.value.trim()) {
        showInputError(patientInput, 'recordPatientError');
        return;
    }
    // FEATURE 2: Check for duplicate patient tag
    const enteredTag = patientInput.value.trim();
    if (checkDuplicateTag(enteredTag)) {
        const proceed = await confirmDuplicateTag(enteredTag);
        if (!proceed) {
            patientInput.focus();
            return;
        }
    }
    startRecording();
}

export async function startRecording() {
    const constraints = { audio: { echoCancellation: true, noiseSuppression: true } };
    const isResume = !!state.resumingSessionId;

    // FIX A1: recordBtn was only hidden AFTER the getUserMedia() await, so a
    // double tap during the permission prompt re-entered this function and ran
    // startTimer() twice. Disable the control (and re-enter guard) before the await.
    if (state.recordingStartInProgress) {
        logger.warn('RECORDING', 'startRecording re-entered while a start was already in flight â€” ignored');
        return;
    }
    state.recordingStartInProgress = true;
    const recordBtnEl = document.getElementById('recordBtn');
    if (recordBtnEl) recordBtnEl.disabled = true;

    logger.info('RECORDING', 'Requesting microphone access', { constraints, resuming: isResume });
    try {
        // BUG 1 FIX: Pre-check mic permission state â€” if previously denied, show guidance immediately
        if (navigator.permissions && navigator.permissions.query) {
            try {
                const permStatus = await navigator.permissions.query({ name: 'microphone' });
                if (permStatus.state === 'denied') {
                    logger.warn('RECORDING', 'Microphone permission previously denied â€” showing manual steps');
                    setStatus('recordStatus', 'Microphone access blocked.', 'error');
                    showMicPermissionError('permission_denied');
                    state.recordingStartInProgress = false;
                    const recordBtnEl2 = document.getElementById('recordBtn');
                    if (recordBtnEl2) recordBtnEl2.disabled = false;
                    return;
                }
            } catch (permErr) {
                // permissions.query may not support 'microphone' on all browsers â€” continue to getUserMedia
            }
        }

        // Fresh recording â€” clear previous segments (unless resuming)
        if (!isResume && !state.recordingInterrupted) {
            resetRecordingSegments(); // FIX A3: blobs + their metadata together
            state.elapsedBeforePause = 0;
            state.currentSessionId = crypto.randomUUID();
            state.currentSessionDbId = null;
        }
        state.recordingInterrupted = false;
        hideInterruptedUI();

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        hideMicPermissionError();
        const track = stream.getAudioTracks()[0];
        const trackSettings = track.getSettings ? track.getSettings() : {};
        logger.info('RECORDING', 'Microphone access granted', {
            trackLabel: track.label,
            trackSettings,
            trackState: track.readyState,
        });

        let mimeType = 'audio/webm;codecs=opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
            logger.warn('RECORDING', 'webm/opus not supported, falling back', { selectedMime: mimeType || 'default' });
        }
        state.mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
        state.audioChunks = [];
        let chunkIndex = 0;

        // FIX A3: a NEW state.mediaRecorder means a NEW container stream. Tag every
        // segment it produces with this generation so combineSegments() can tell
        // a resumed recording apart from one continuous recorder.
        state.recorderGeneration++;
        const thisGeneration = state.recorderGeneration;
        const recorderMimeType = state.mediaRecorder.mimeType || mimeType || '';

        logger.info('RECORDING', 'state.mediaRecorder created', {
            mimeType: state.mediaRecorder.mimeType, state: state.mediaRecorder.state, generation: thisGeneration,
        });

        state.mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
                state.audioChunks.push(e.data);
                chunkIndex++;
                logger.info('RECORDING', 'Data chunk received', { chunkIndex, chunkSize: e.data.size, totalChunks: state.audioChunks.length });
            }
        };

        state.mediaRecorder.onstart = () => {
            logger.info('RECORDING', 'state.mediaRecorder started', { state: state.mediaRecorder.state });
        };

        state.mediaRecorder.onpause = () => {
            logger.info('RECORDING', 'state.mediaRecorder paused');
        };

        state.mediaRecorder.onresume = () => {
            logger.info('RECORDING', 'state.mediaRecorder resumed');
        };

        state.mediaRecorder.onerror = (event) => {
            logger.error('RECORDING', 'state.mediaRecorder error event', event.error || new Error('Unknown state.mediaRecorder error'), {
                state: state.mediaRecorder.state,
                errorEvent: event.type,
                failurePoint: 'recording',
            });
        };

        state.mediaRecorder.onstop = async () => {
            stream.getTracks().forEach(t => t.stop());
            stopAutoSave();

            const segmentBlob = new Blob(state.audioChunks, { type: state.mediaRecorder.mimeType });
            const totalSize = state.audioChunks.reduce((sum, c) => sum + c.size, 0);
            const duration = totalElapsedMs(); // FIX A4: monotonic, clamped at 0
            const bytesPerSecond = duration > 0 ? Math.round(totalSize / (duration / 1000)) : 0;
            logger.info('RECORDING', 'state.mediaRecorder stopped', {
                totalChunks: state.audioChunks.length,
                segmentSize: segmentBlob.size,
                totalRawSize: totalSize,
                durationMs: duration,
                bytesPerSecond,
                interrupted: state.recordingInterrupted,
                existingSegments: state.recordingSegments.length,
                existingSegmentsSize: state.recordingSegments.reduce((s, b) => s + b.size, 0),
            });

            // Detect suspicious conditions: recording has duration but no data
            if (duration > 5000 && segmentBlob.size === 0 && state.recordingSegments.length === 0) {
                logger.warn('RECORDING', 'SUSPICIOUS: Recording has duration but NO audio data captured', {
                    durationMs: duration, chunks: state.audioChunks.length, segmentSize: segmentBlob.size,
                });
            }

            if (state.recordingInterrupted) {
                pushRecordingSegment(segmentBlob, state.mediaRecorder.mimeType, thisGeneration); // FIX A3
                await saveSessionSegment(segmentBlob, duration);
                if (state.currentSessionDbId) await SessionStore.update(state.currentSessionDbId, { status: 'interrupted', totalDurationMs: duration }).catch(() => {});
                showInterruptedUI();
                loadRecordingHistory();
                return;
            }

            pushRecordingSegment(segmentBlob, state.mediaRecorder.mimeType, thisGeneration); // FIX A3
            await saveSessionSegment(segmentBlob, duration);
            if (state.currentSessionDbId) await SessionStore.update(state.currentSessionDbId, { status: 'pending-upload', totalDurationMs: duration }).catch(() => {});
            const finalBlob = await combineSegments();
            loadRecordingHistory();
            await uploadAndProcess(finalBlob, 'record', state.lastSegmentJoinInfo); // FIX A3: pass audio metadata
        };

        // Detect unexpected interruption (e.g., phone call, OS revokes mic)
        track.onended = () => {
            if (state.isRecording || state.isPaused) {
                logger.warn('RECORDING', 'Audio track ended unexpectedly (interruption detected)', {
                    trackState: track.readyState,
                    elapsed: elapsedSinceStart(), // FIX A4: monotonic
                    segmentsSoFar: state.recordingSegments.length,
                    generation: thisGeneration,
                });
                state.recordingInterrupted = true;
                state.elapsedBeforePause += elapsedSinceStart(); // FIX A4
                state.startTime = null;
                state.startTimeMono = null;
                state.isRecording = false;
                state.isPaused = false;
                document.getElementById('recordBtn').classList.remove('recording');
                document.getElementById('recordBtn').style.display = '';
                hideRecordingControls();
                document.getElementById('timer').classList.remove('paused');
                stopTimer();
                releaseWakeLock();
                if (state.mediaRecorder && state.mediaRecorder.state !== 'inactive') {
                    state.mediaRecorder.stop();
                }
            }
        };

        // Create session in IndexedDB if new recording
        if (!state.currentSessionDbId) {
            const patientLabel = document.getElementById('patientLabel').value.trim() || 'visit';
            const linkedPatientId = getRecordPatientId(); // null unless a patient was selected/created
            const sessionRecord = {
                sessionId: state.currentSessionId,
                status: 'recording',
                patientLabel,
                profileId: currentProfileId || 'default',
                segments: [],
                mimeType: state.mediaRecorder.mimeType || 'audio/webm',
                startedAt: new Date().toISOString(),
                lastSavedAt: new Date().toISOString(),
                totalDurationMs: 0,
                uploadedNoteId: null,
            };
            // FEAT-002: only extend the record shape when a patient was actually
            // selected — absent key keeps the create request identical to today.
            if (linkedPatientId) sessionRecord.patientId = linkedPatientId;
            try {
                state.currentSessionDbId = await SessionStore.create(sessionRecord);
                logger.info('RECORDING', 'Session created in IndexedDB', { id: state.currentSessionDbId, sessionId: state.currentSessionId });
            } catch (err) {
                logger.warn('RECORDING', 'Failed to create session in IndexedDB', { error: err.message });
            }
        } else {
            // FIX A3: no silent swallow in the recording path
            try {
                await SessionStore.update(state.currentSessionDbId, { status: 'recording' });
            } catch (err) {
                surfacePersistFailure('resume session status', err);
            }
        }

        state.mediaRecorder.start(5000);
        state.isRecording = true;
        state.isPaused = false;
        document.getElementById('recordBtn').classList.add('recording');
        document.getElementById('recordBtn').style.display = 'none';
        showRecordingControls();
        setStatus('recordStatus', "Recording... Don't lock your phone", 'recording');
        startTimer();
        await requestWakeLock();
        startAutoSave();
        state.resumingSessionId = null;
        loadRecordingHistory();
    } catch (err) {
        let errorType = 'unknown';
        if (err.name === 'NotAllowedError') errorType = 'permission_denied';
        else if (err.name === 'NotFoundError') errorType = 'no_microphone';
        else if (err.name === 'NotReadableError') errorType = 'mic_in_use';
        else if (err.name === 'OverconstrainedError') errorType = 'constraints_unsatisfied';
        logger.error('RECORDING', 'Failed to start recording: ' + errorType, err, { errorType, constraints, failurePoint: 'recording' });
        const statusMsg = errorType === 'constraints_unsatisfied'
            ? 'Microphone settings not supported on this device. Retrying...'
            : 'Microphone access denied.';
        setStatus('recordStatus', statusMsg, 'error');
        showMicPermissionError(errorType);
    } finally {
        // FIX A1: release the re-entry guard. Safe to re-enable the button here â€”
        // it is already hidden while recording and toggleRecording() refuses to
        // start a second recording while state.isRecording/state.isPaused is true.
        state.recordingStartInProgress = false;
        if (recordBtnEl) recordBtnEl.disabled = false;
    }
}

// --- Auto-save: flush buffer every 10 seconds for crash protection ---
export function startAutoSave() {
    stopAutoSave();
    state.autoSaveInterval = setInterval(async () => {
        if (!state.isRecording || !state.mediaRecorder || state.mediaRecorder.state !== 'recording') return;
        try {
            state.mediaRecorder.requestData();
            await new Promise(r => setTimeout(r, 300));
            if (state.audioChunks.length === 0) return;

            const segmentBlob = new Blob(state.audioChunks, { type: state.mediaRecorder.mimeType || 'audio/webm' });
            if (segmentBlob.size === 0) return;

            const elapsed = totalElapsedMs(); // FIX A4: monotonic
            pushRecordingSegment(segmentBlob, state.mediaRecorder.mimeType, state.recorderGeneration); // FIX A3
            state.audioChunks = [];

            if (state.currentSessionDbId) {
                await SessionStore.appendSegment(state.currentSessionDbId, segmentBlob, elapsed);
                logger.info('RECORDING', 'Auto-saved segment', { segmentSize: segmentBlob.size, totalDuration: elapsed });
            }
            showAutoSaveIndicator();
        } catch (err) {
            // FIX A3: a failed persist is NOT non-fatal â€” the user must know before
            // they finish the visit. All three save paths behave identically now.
            surfacePersistFailure('auto-save', err);
        }
    }, 10000);
}

/**
 * FIX A3: one place to report a failed IndexedDB write (quota, private browsing,
 * closed DB). Logs at ERROR level and tells the user, because silently dropping
 * a segment loses part of a real visit recording.
 */
export function surfacePersistFailure(phase, err) {
    logger.error('RECORDING', 'Failed to save audio segment locally (' + phase + ')', err || null, {
        phase,
        errorName: err && err.name,
        segments: state.recordingSegments.length,
    });
    setStatus('recordStatus',
        '\u26a0\ufe0f Could not save audio to this device (' + phase + '). Keep the tab open and finish the recording â€” storage may be full or in private mode.',
        'error');
}

export function stopAutoSave() {
    if (state.autoSaveInterval) { clearInterval(state.autoSaveInterval); state.autoSaveInterval = null; }
}

export async function saveSessionSegment(blob, durationMs) {
    if (!state.currentSessionDbId || !blob || blob.size === 0) return;
    try {
        await SessionStore.appendSegment(state.currentSessionDbId, blob, durationMs);
    } catch (err) {
        // FIX A3: was a bare `catch (err) {}` that silently dropped the segment.
        surfacePersistFailure('segment save', err);
    }
}

export function showAutoSaveIndicator() {
    const indicator = document.getElementById('autoSaveIndicator');
    if (!indicator) return;
    indicator.classList.add('visible');
    setTimeout(() => indicator.classList.remove('visible'), 2000);
}

export function stopRecording() {
    const duration = totalElapsedMs(); // FIX A4: monotonic
    const totalDataSize = state.audioChunks.reduce((sum, c) => sum + c.size, 0);
    logger.info('RECORDING', 'Intentional stop', { durationMs: duration, totalDataSize, chunks: state.audioChunks.length, segments: state.recordingSegments.length });
    // Log point 2: Recording duration:size ratio for detecting corrupt/empty recordings
    const bytesPerSecond = duration > 0 ? Math.round(totalDataSize / (duration / 1000)) : 0;
    const ratioData = { durationMs: duration, totalBytes: totalDataSize, bytesPerSecond, chunks: state.audioChunks.length, segments: state.recordingSegments.length };
    if (bytesPerSecond < 100 && duration > 5000) {
        logger.warn('RECORDING', 'Abnormal recording ratio: less than 100 bytes/sec', ratioData);
    } else {
        logger.info('RECORDING', 'Recording duration:size ratio', ratioData);
    }
    state.recordingInterrupted = false;
    state.isRecording = false;
    state.isPaused = false;
    document.getElementById('recordBtn').classList.remove('recording');
    document.getElementById('recordBtn').style.display = '';
    hideRecordingControls();
    document.getElementById('timer').classList.remove('paused');
    stopTimer();
    releaseWakeLock();
    setStatus('recordStatus', 'Processing recording...', 'uploading');
    if (state.mediaRecorder && state.mediaRecorder.state !== 'inactive') state.mediaRecorder.stop();
}

// --- Pause/Resume/Get Note controls ---
export function togglePauseResume() {
    if (state.isPaused) {
        resumeRecordingFromPause();
    } else if (state.isRecording) {
        pauseRecording();
    }
}

export function pauseRecording() {
    if (!state.mediaRecorder || state.mediaRecorder.state !== 'recording') return;
    logger.info('RECORDING', 'Pausing recording');

    // Pause state.mediaRecorder
    state.mediaRecorder.pause();

    // Freeze timer â€” FIX A4: monotonic elapsed, clamped at 0
    state.elapsedBeforePause += elapsedSinceStart();
    state.startTime = null;
    state.startTimeMono = null;
    stopTimer();

    // Flush current state.audioChunks to IndexedDB immediately
    flushChunksOnPause();

    // Stop auto-save during pause
    stopAutoSave();

    // Update state
    state.isRecording = false;
    state.isPaused = true;

    // Update SessionStore status
    if (state.currentSessionDbId) {
        SessionStore.update(state.currentSessionDbId, { status: 'paused' }).catch(() => {});
    }

    // Update UI
    document.getElementById('pauseIcon').classList.add('hidden');
    document.getElementById('resumeIcon').classList.remove('hidden');
    document.getElementById('timer').classList.add('paused');
    setStatus('recordStatus', 'Recording paused', '');

    // v2.11.0 FIX A: Enable Get Note on pause â€” doctor can now tap it
    const getNoteBtn = document.getElementById('btnGetNote');
    if (getNoteBtn) { getNoteBtn.disabled = false; getNoteBtn.classList.remove('disabled'); }

    // v2.11.0 FIX B: Keep wake lock active during pause â€” screen must stay on
    requestWakeLock();
}

export function resumeRecordingFromPause() {
    if (!state.mediaRecorder || state.mediaRecorder.state !== 'paused') return;
    logger.info('RECORDING', 'Resuming recording from pause');

    // FIX 5: Restore elapsed time from IndexedDB if lost after tab freeze
    // performance.now() resets on page reload, so state.elapsedBeforePause may be stale
    if (state.currentSessionDbId && (state.elapsedBeforePause === 0 || state.elapsedBeforePause === undefined)) {
        SessionStore.get(state.currentSessionDbId).then(session => {
            if (session && session.totalDurationMs > 0 && state.elapsedBeforePause < session.totalDurationMs) {
                state.elapsedBeforePause = session.totalDurationMs;
                logger.info('RECORDING', 'Restored elapsed time from IndexedDB', { totalDurationMs: session.totalDurationMs });
            }
        }).catch(() => {});
    }

    // Resume state.mediaRecorder
    state.mediaRecorder.resume();

    // Resume timer (startTimer sets both the wall-clock and monotonic bases)
    startTimer();

    // Restart auto-save
    startAutoSave();

    // Update state
    state.isRecording = true;
    state.isPaused = false;

    // Update SessionStore status
    if (state.currentSessionDbId) {
        SessionStore.update(state.currentSessionDbId, { status: 'recording' }).catch(() => {});
    }

    // Update UI
    document.getElementById('pauseIcon').classList.remove('hidden');
    document.getElementById('resumeIcon').classList.add('hidden');
    document.getElementById('timer').classList.remove('paused');
    setStatus('recordStatus', "Recording... Don't lock your phone", 'recording');

    // v2.11.0 FIX A: Disable Get Note again while actively recording
    const getNoteBtn = document.getElementById('btnGetNote');
    if (getNoteBtn) { getNoteBtn.disabled = true; getNoteBtn.classList.add('disabled'); }
}

export async function flushChunksOnPause() {
    try {
        // Request any pending data from state.mediaRecorder
        if (state.mediaRecorder && state.mediaRecorder.state === 'paused') {
            // Data should already be available from ondataavailable
        }
        await new Promise(r => setTimeout(r, 200));
        if (state.audioChunks.length === 0) return;

        const segmentBlob = new Blob(state.audioChunks, { type: state.mediaRecorder.mimeType || 'audio/webm' });
        if (segmentBlob.size === 0) return;

        pushRecordingSegment(segmentBlob, state.mediaRecorder.mimeType, state.recorderGeneration); // FIX A3
        state.audioChunks = [];

        if (state.currentSessionDbId) {
            await SessionStore.appendSegment(state.currentSessionDbId, segmentBlob, state.elapsedBeforePause);
            logger.info('RECORDING', 'Flushed chunks on pause', { segmentSize: segmentBlob.size, totalDuration: state.elapsedBeforePause });
        }
        showAutoSaveIndicator();
    } catch (err) {
        // FIX A3: consistent with the other two save paths â€” visible, not swallowed.
        surfacePersistFailure('pause flush', err);
    }
}

export function getNote() {
    logger.info('RECORDING', 'Get Note tapped', { isPaused: state.isPaused, isRecording: state.isRecording, mode: state.recordingMode });

    // Audio Upload mode: stop recording and upload
    if (state.isPaused) {
        state.isPaused = false;
        state.isRecording = true;
    }
    stopRecording();
}

// --- Recording Controls UI helpers ---
export function showRecordingControls() {
    document.getElementById('recordingControls').classList.remove('hidden');
    // Reset to pause state (showing pause icon)
    document.getElementById('pauseIcon').classList.remove('hidden');
    document.getElementById('resumeIcon').classList.add('hidden');
    // v2.11.0 FIX A: Disable Get Note while actively recording â€” must pause first
    const getNoteBtn = document.getElementById('btnGetNote');
    if (getNoteBtn) { getNoteBtn.disabled = true; getNoteBtn.classList.add('disabled'); }
}

export function hideRecordingControls() {
    document.getElementById('recordingControls').classList.add('hidden');
}

/**
 * FIX A3 â€” segment join.
 *
 * The normal path is ONE continuous MediaRecorder: requestData() splits the
 * stream into byte-adjacent segments, and concatenating those is valid. That
 * path is unchanged.
 *
 * The broken paths are resumeRecording() and resumeFromHistory(), which build a
 * NEW state.mediaRecorder while keeping the old segments. Concatenating two independent
 * container streams (two WebM headers, or a WebM stream followed by an MP4 one)
 * produces a file Transcribe rejects with "the data in your input media file
 * isn't valid" â€” the exact failure seen in production.
 *
 * RECOVERY STRATEGY CHOSEN: upload the longest contiguous same-generation,
 * same-mimeType run and tell the user plainly that the rest was not included.
 *  - Uploading segments as separate parts needs server-side concatenation that
 *    does not exist today (and this change must stay additive).
 *  - Blocking the upload outright loses ALL the audio for that visit.
 *  - The longest run keeps the largest single usable piece of audio, and the
 *    segments left behind are NOT deleted: the session stays in Recent
 *    Recordings so it can still be saved/downloaded and handled manually.
 * Silent corruption is the one outcome we refuse.
 */
export async function combineSegments() {
    const totalSize = state.recordingSegments.reduce((sum, s) => sum + s.size, 0);
    logger.info('RECORDING', 'combineSegments called', {
        segmentCount: state.recordingSegments.length,
        segmentSizes: state.recordingSegments.map(s => s.size),
        totalSize,
        generations: state.recordingSegmentMeta.map(m => m.generation),
    });

    if (state.recordingSegments.length === 0) {
        logger.warn('RECORDING', 'combineSegments called with EMPTY state.recordingSegments array');
        state.lastSegmentJoinInfo = { segmentCount: 0, mimeType: '', blobSize: 0, generationCount: 0, droppedSegments: 0, droppedBytes: 0 };
        return new Blob([], { type: 'audio/webm' });
    }

    // Pick the run of segments to upload.
    let segments = state.recordingSegments;
    let metas = state.recordingSegmentMeta.length === state.recordingSegments.length
        ? state.recordingSegmentMeta
        : state.recordingSegments.map(b => ({ size: b.size, mimeType: b.type || '', generation: 0 }));

    const distinctGenerations = new Set(metas.map(m => m.generation));
    const distinctMimeTypes = new Set(metas.map(m => normalizeMimeType(m.mimeType || '')));
    let droppedSegments = 0;
    let droppedBytes = 0;

    if (distinctGenerations.size > 1 || distinctMimeTypes.size > 1) {
        const run = longestContiguousRun(metas);
        droppedSegments = segments.length - (run.end - run.start);
        droppedBytes = totalSize - metas.slice(run.start, run.end).reduce((sum, m) => sum + (m.size || 0), 0);
        segments = segments.slice(run.start, run.end);
        metas = metas.slice(run.start, run.end);

        logger.error('RECORDING', 'Segments span multiple recorders/containers â€” uploading longest usable run only', null, {
            totalSegments: state.recordingSegments.length,
            keptSegments: segments.length,
            droppedSegments,
            droppedBytes,
            generations: Array.from(distinctGenerations),
            mimeTypes: Array.from(distinctMimeTypes),
        });
        setStatus('recordStatus',
            '\u26a0\ufe0f This recording was restarted mid-visit, so it holds ' + distinctGenerations.size +
            ' separate audio streams that cannot be merged. Uploading the longest part (' +
            (segments.reduce((s, b) => s + b.size, 0) / 1024 / 1024).toFixed(1) + ' MB). The other ' +
            droppedSegments + ' segment' + (droppedSegments === 1 ? '' : 's') +
            ' (' + (droppedBytes / 1024 / 1024).toFixed(1) + ' MB) was NOT included â€” it is still saved under Recent Recordings.',
            'error');
    }

    // FIX A3: keep the FULL mimeType including ';codecs=opus'. Stripping the codec
    // parameter is what let a WebM/Opus stream be presented as a bare container.
    const mimeType = (metas[0] && metas[0].mimeType) || segments[0].type || 'audio/webm';

    const compositeBlob = segments.length === 1
        ? segments[0]
        : new Blob(segments, { type: mimeType });

    // SAFARI FIX (unchanged): consolidate into a single contiguous blob. Safari
    // reports the right .size for a multi-part Blob built from IndexedDB segments
    // but then fails to read the bytes during upload.
    let finalBlob;
    try {
        const arrayBuffer = await compositeBlob.arrayBuffer();
        finalBlob = new Blob([arrayBuffer], { type: mimeType });
        logger.info('RECORDING', 'Segments combined and consolidated for Safari', {
            segments: segments.length,
            originalSize: compositeBlob.size,
            consolidatedSize: finalBlob.size,
            mimeType,
        });
    } catch (consolidateErr) {
        logger.warn('RECORDING', 'Blob consolidation failed, using composite blob', { error: consolidateErr.message });
        finalBlob = new Blob([compositeBlob], { type: mimeType });
    }

    // FALLBACK SAFETY NET: binary container sniff. If metadata-based longestContiguousRun
    // missed the problem (e.g., IndexedDB metadata lost), detect multiple container headers
    // in the final blob and keep only the largest valid part.
    const sniffResult = await sniffAndSplit(finalBlob);
    if (!sniffResult.valid) {
        if (sniffResult.parts.length === 0) {
            logger.error('RECORDING', 'sniffAndSplit: blob has no valid container header — completely corrupted', null, {
                blobSize: finalBlob.size, mimeType,
            });
            throw new Error('Recording is corrupted (no valid audio container found). Your segments are saved under Recent Recordings — use Save Audio to recover manually.');
        }
        // Pick the largest valid part
        let largest = sniffResult.parts[0];
        for (let i = 1; i < sniffResult.parts.length; i++) {
            if (sniffResult.parts[i].size > largest.size) largest = sniffResult.parts[i];
        }
        const sniffDroppedBytes = finalBlob.size - largest.size;
        const sniffDroppedParts = sniffResult.parts.length - 1;
        droppedSegments += sniffDroppedParts;
        droppedBytes += sniffDroppedBytes;
        finalBlob = largest;

        logger.error('RECORDING', 'sniffAndSplit detected multi-container concat — using largest part', null, {
            totalParts: sniffResult.parts.length,
            largestSize: largest.size,
            droppedBytes: sniffDroppedBytes,
            originalSize: sniffResult.parts.reduce((s, p) => s + p.size, 0),
        });
        setStatus('recordStatus',
            '\u26a0\ufe0f Multiple audio streams detected in recording (likely caused by an interruption). ' +
            'Uploading the largest part (' + (largest.size / 1024 / 1024).toFixed(1) + ' MB). ' +
            sniffDroppedParts + ' smaller part' + (sniffDroppedParts === 1 ? '' : 's') +
            ' (' + (sniffDroppedBytes / 1024 / 1024).toFixed(1) + ' MB) not included.',
            'error');
    }

    state.lastSegmentJoinInfo = {
        segmentCount: segments.length,
        mimeType,
        blobSize: finalBlob.size,
        generationCount: new Set(metas.map(m => m.generation)).size,
        droppedSegments,
        droppedBytes,
    };
    return finalBlob;
}

/**
 * FIX A3: longest run of consecutive segments sharing one generation AND one
 * normalized mimeType. Returns { start, end } as a half-open range.
 */
export function longestContiguousRun(metas) {
    let best = { start: 0, end: 0, bytes: -1 };
    let i = 0;
    while (i < metas.length) {
        let j = i + 1;
        while (j < metas.length &&
               metas[j].generation === metas[i].generation &&
               normalizeMimeType(metas[j].mimeType) === normalizeMimeType(metas[i].mimeType)) {
            j++;
        }
        let bytes = 0;
        for (let k = i; k < j; k++) bytes += metas[k].size || 0;
        if (bytes > best.bytes) best = { start: i, end: j, bytes };
        i = j;
    }
    if (best.bytes < 0) best = { start: 0, end: metas.length, bytes: 0 };
    return best;
}

/**
 * Binary container sniff — safety net for when metadata-based generation tracking
 * fails (e.g., IndexedDB connection lost mid-recording). Scans a blob for container
 * header signatures and splits at boundaries if multiple are found.
 *
 * Returns { valid: true, blob } if single valid container,
 * or { valid: false, parts: [blob1, blob2, ...] } if multiple containers detected,
 * or { valid: false, parts: [] } if no valid header at all.
 *
 * ponytail: O(n) scan over the full blob — runs once at upload time, acceptable
 * for 22MB worst case. Upgrade path: use a Worker if blobs exceed ~100MB.
 */
export async function sniffAndSplit(blob) {
    if (!blob || blob.size < 12) return { valid: false, parts: [] };

    const header = new Uint8Array(await blob.slice(0, 12).arrayBuffer());

    // Detect container format from first bytes
    const isMp4 = header[4] === 0x66 && header[5] === 0x74 && header[6] === 0x79 && header[7] === 0x70;
    const isWebm = header[0] === 0x1A && header[1] === 0x45 && header[2] === 0xDF && header[3] === 0xA3;

    if (!isMp4 && !isWebm) {
        return { valid: false, parts: [] };
    }

    // Scan for additional container headers within the blob.
    // Start after first 1KB to skip the initial header region and avoid false positives.
    const fullBuffer = new Uint8Array(await blob.arrayBuffer());
    const headerPositions = [0];

    for (let pos = 1024; pos < fullBuffer.length - 8; pos++) {
        if (isMp4) {
            // MP4: look for 'ftyp' at offset+4 from a box boundary
            if (fullBuffer[pos + 4] === 0x66 && fullBuffer[pos + 5] === 0x74 &&
                fullBuffer[pos + 6] === 0x79 && fullBuffer[pos + 7] === 0x70) {
                headerPositions.push(pos);
            }
        } else {
            // WebM/Matroska: EBML header signature at offset 0
            if (fullBuffer[pos] === 0x1A && fullBuffer[pos + 1] === 0x45 &&
                fullBuffer[pos + 2] === 0xDF && fullBuffer[pos + 3] === 0xA3) {
                headerPositions.push(pos);
            }
        }
    }

    if (headerPositions.length === 1) {
        return { valid: true, blob };
    }

    // Split at header boundaries, discard tiny fragments (<1KB)
    const parts = [];
    for (let i = 0; i < headerPositions.length; i++) {
        const start = headerPositions[i];
        const end = i + 1 < headerPositions.length ? headerPositions[i + 1] : fullBuffer.length;
        if (end - start > 1024) {
            parts.push(new Blob([fullBuffer.slice(start, end)], { type: blob.type }));
        }
    }
    return { valid: false, parts };
}

// --- Interrupted Recording UI ---
export function showInterruptedUI() {
    const container = document.getElementById('interruptedActions');
    container.classList.remove('hidden');
    const segCount = state.recordingSegments.length;
    const totalSize = state.recordingSegments.reduce((sum, b) => sum + b.size, 0);
    const sizeMB = (totalSize / 1024 / 1024).toFixed(1);
    setStatus('recordStatus',
        '\u26a0\ufe0f Recording interrupted! (' + segCount + ' segment' + (segCount > 1 ? 's' : '') + ', ' + sizeMB + ' MB captured)',
        'error');
}

export function hideInterruptedUI() {
    const container = document.getElementById('interruptedActions');
    if (container) container.classList.add('hidden');
}

export function resumeRecording() {
    state.resumingSessionId = state.currentSessionDbId;
    state.recordingInterrupted = true; // Keep existing segments
    document.getElementById('recordBtn').style.display = '';
    hideRecordingControls();
    startRecording();
}

export async function finishAndUpload() {
    hideInterruptedUI();
    const finalBlob = await combineSegments();
    if (finalBlob.size === 0) {
        setStatus('recordStatus', 'No audio captured.', 'error');
        return;
    }
    if (state.currentSessionDbId) await SessionStore.update(state.currentSessionDbId, { status: 'pending-upload' }).catch(() => {});
    // FIX A3: don't overwrite the cross-generation warning combineSegments may have set
    if (!state.lastSegmentJoinInfo || !state.lastSegmentJoinInfo.droppedSegments) {
        setStatus('recordStatus', 'Processing recording...', 'uploading');
    }
    loadRecordingHistory();
    uploadAndProcess(finalBlob, 'record', state.lastSegmentJoinInfo); // FIX A3: pass audio metadata
}

export function discardRecording() {
    hideInterruptedUI();
    hideRecordingControls();
    if (state.currentSessionDbId) {
        SessionStore.remove(state.currentSessionDbId).catch(() => {});
        state.currentSessionDbId = null;
    }
    resetRecordingSegments(); // FIX A3
    state.elapsedBeforePause = 0;
    state.isPaused = false;
    state.currentSessionId = null;
    state.startTime = null;
    state.startTimeMono = null; // FIX A1: no stale timer base left behind
    document.getElementById('recordBtn').style.display = '';
    document.getElementById('timer').classList.remove('paused');
    setStatus('recordStatus', 'Recording discarded. Tap to start again.', '');
    document.getElementById('timer').textContent = '00:00:00';
    loadRecordingHistory();
}


export async function migrateLegacyRecordings() {
    try {
        const legacy = await RecordingStore.getAll();
        if (legacy.length === 0) return;
        logger.info('SYSTEM', 'Migrating legacy recordings', { count: legacy.length });
        for (const entry of legacy) {
            const sessionRecord = {
                sessionId: crypto.randomUUID(),
                status: 'pending-upload',
                patientLabel: entry.patientLabel || 'visit',
                segments: [entry.blob],
                mimeType: entry.mimeType || 'audio/webm',
                startedAt: new Date(entry.timestamp || Date.now()).toISOString(),
                lastSavedAt: new Date(entry.timestamp || Date.now()).toISOString(),
                totalDurationMs: 0,
                uploadedNoteId: null,
            };
            await SessionStore.create(sessionRecord);
            await RecordingStore.remove(entry.id);
        }
        logger.info('SYSTEM', 'Legacy migration complete', { migrated: legacy.length });
        loadRecordingHistory();
    } catch (err) {
        logger.warn('SYSTEM', 'Legacy migration failed (non-fatal)', { error: err.message });
    }
}

// Mark stale 'recording' sessions as 'interrupted' and reconcile stale 'uploading' sessions
export async function recoverStaleSessions() {
    try {
        const all = await SessionStore.getAll();
        for (const session of all) {
            if (session.status === 'recording' || session.status === 'paused') {
                await SessionStore.update(session.id, { status: 'interrupted' });
                logger.info('SYSTEM', 'Recovered stale recording session', { id: session.id, sessionId: session.sessionId });
            }
        }
        // Reconcile stale 'uploading' sessions â€” check if note was actually created
        const uploadingSessions = all.filter(s => s.status === 'uploading');
        if (uploadingSessions.length > 0) {
            await reconcileUploadingSessions(uploadingSessions);
        }
    } catch (err) {
        logger.warn('SYSTEM', 'Stale session recovery failed', { error: err.message });
    }
}

// Check stale 'uploading' sessions against the /notes API to see if they actually completed
export async function reconcileUploadingSessions(sessions) {
    try {
        const res = await apiFetch('/notes');
        if (!res.ok) {
            // Can't verify â€” mark as pending-upload so user can retry
            for (const session of sessions) {
                await SessionStore.update(session.id, { status: 'pending-upload' });
            }
            loadRecordingHistory();
            return;
        }
        const data = await res.json();
        const notes = data.notes || [];

        for (const session of sessions) {
            // Check if a note exists with matching patientLabel
            const matchingNote = notes.find(n =>
                n.patientLabel && session.patientLabel &&
                n.patientLabel.toLowerCase() === session.patientLabel.toLowerCase()
            );
            if (matchingNote) {
                // Note exists â€” upload succeeded, mark as completed
                await SessionStore.update(session.id, { status: 'completed', uploadedNoteId: matchingNote.noteId });
                logger.info('SYSTEM', 'Reconciled uploading session as completed', { id: session.id, noteId: matchingNote.noteId });
            } else {
                // No matching note â€” upload likely failed before completion, allow retry
                await SessionStore.update(session.id, { status: 'pending-upload' });
                // FIX N1: patient label removed â€” it reached console.log and the exportable log buffer
                logger.info('SYSTEM', 'Reconciled uploading session as pending-upload', { id: session.id });
            }
        }
        loadRecordingHistory();
    } catch (err) {
        // Network error â€” mark as pending-upload to be safe
        logger.warn('SYSTEM', 'Reconciliation failed, marking as pending-upload', { error: err.message });
        for (const session of sessions) {
            try { await SessionStore.update(session.id, { status: 'pending-upload' }); } catch (e) {}
        }
        loadRecordingHistory();
    }
}

// Load and render recording history on the Record page
export async function loadRecordingHistory() {
    const container = document.getElementById('recordingHistory');
    if (!container) return;
    try {
        let sessions = await SessionStore.getAll();
        // Exclude the currently active session (it's shown in the main recording UI)
        if (state.currentSessionDbId) {
            sessions = sessions.filter(s => s.id !== state.currentSessionDbId);
        }
        // Filter by current profile (old sessions without profileId show under 'default')
        const activeProfile = currentProfileId || 'default';
        sessions = sessions.filter(s => (s.profileId || 'default') === activeProfile);
        sessions.sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt));

        hideRecoveryBanner();

        if (sessions.length === 0) {
            container.innerHTML = '';
            return;
        }

        container.innerHTML = '<h3 class="history-title">Recent Recordings</h3>' +
            sessions.map(session => renderHistoryCard(session)).join('');
    } catch (err) {
        logger.warn('SYSTEM', 'Failed to load recording history', { error: err.message });
    }
}

export function renderHistoryCard(session) {
    const date = new Date(session.startedAt);
    const dateStr = date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const duration = formatSessionDuration(session.totalDurationMs || 0);
    const segCount = (session.segments || []).length;
    const totalSize = (session.segments || []).reduce((sum, s) => sum + (s.size || 0), 0);
    const sizeMB = (totalSize / 1024 / 1024).toFixed(1);

    let statusBadge = '';
    let actions = '';

    switch (session.status) {
        case 'recording':
        case 'paused':
        case 'interrupted':
            statusBadge = '<span class="session-badge badge-interrupted">Interrupted</span>';
            actions = `
                <button class="btn-history-resume" data-action="resume-from-history" data-id="${session.id}">&#9654; Resume</button>
                <button class="btn-history-upload" data-action="upload-from-history" data-id="${session.id}">&#8593; Upload As-Is</button>
                <button class="btn-history-download" data-action="download-from-history" data-id="${session.id}" title="Save audio file">&#11015; Save Audio</button>
                <button class="btn-history-discard" data-action="discard-from-history" data-id="${session.id}">&#10005; Discard</button>`;
            break;
        case 'pending-upload':
            statusBadge = '<span class="session-badge badge-pending">Pending Upload</span>';
            actions = `
                <button class="btn-history-upload" data-action="upload-from-history" data-id="${session.id}">&#8593; Upload</button>
                <button class="btn-history-download" data-action="download-from-history" data-id="${session.id}" title="Save audio file">&#11015; Save Audio</button>
                <button class="btn-history-discard" data-action="discard-from-history" data-id="${session.id}">&#10005; Discard</button>`;
            break;
        case 'uploading':
            statusBadge = '<span class="session-badge badge-uploading">Uploading...</span>';
            actions = `<button class="btn-history-download" data-action="download-from-history" data-id="${session.id}" title="Save audio file">&#11015; Save Audio</button>`;
            break;
        case 'processing':
            statusBadge = '<span class="session-badge badge-processing">Processing...</span>';
            actions = `<button class="btn-history-download" data-action="download-from-history" data-id="${session.id}" title="Save audio file">&#11015; Save Audio</button>`;
            break;
        case 'completed':
            statusBadge = '<span class="session-badge badge-completed">&#10003; Note Created</span>';
            if (session.uploadedNoteId) {
                actions = `<button class="btn btn-sm btn-outline-primary" data-action="view-note" data-id="${session.uploadedNoteId}">&#128196; View Note</button>`;
            }
            break;
    }

    return `<div class="history-card" data-session-id="${session.id}">
        <div class="history-card-info">
            <div class="history-card-label">${escapeHtml(session.patientLabel || 'visit')}</div>
            <div class="history-card-meta">${dateStr} &bull; ${duration} &bull; ${segCount} seg &bull; ${sizeMB} MB</div>
        </div>
        <div class="history-card-status">${statusBadge}</div>
        <div class="history-card-actions">${actions}</div>
    </div>`;
}

export function formatSessionDuration(ms) {
    if (ms <= 0) return '0:00';
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return min + ':' + sec.toString().padStart(2, '0');
}

export async function resumeFromHistory(sessionId) {
    try {
        const session = await SessionStore.get(sessionId);
        if (!session) { loadRecordingHistory(); return; }

        // CRITICAL: Stop any existing recording/timer first
        stopTimer();
        stopAutoSave();
        if (state.mediaRecorder && state.mediaRecorder.state !== 'inactive') {
            state.recordingInterrupted = false;
            try { state.mediaRecorder.stop(); } catch(e) {}
        }
        state.mediaRecorder = null;
        state.isRecording = false;
        state.isPaused = false;

        // Validate and load segments
        const validSegments = (session.segments || []).filter(seg => seg && (typeof seg.size === 'number' ? seg.size > 0 : false));
        // FIX A3: tag adopted segments so combineSegments() can see that the
        // recorder about to be created is a DIFFERENT container stream.
        adoptRecordingSegments(validSegments, session.mimeType);
        state.elapsedBeforePause = session.totalDurationMs || 0;
        state.currentSessionDbId = session.id;
        state.currentSessionId = session.sessionId;
        state.resumingSessionId = session.id;

        document.getElementById('patientLabel').value = session.patientLabel || '';

        // Show the elapsed time on the timer (but don't start it yet â€” startRecording will do that)
        const e = state.elapsedBeforePause;
        document.getElementById('timer').textContent =
            pad(Math.floor(e / 3600000)) + ':' + pad(Math.floor((e % 3600000) / 60000)) + ':' + pad(Math.floor((e % 60000) / 1000));

        state.recordingInterrupted = true;
        startRecording();
    } catch (err) {
        logger.error('RECORDING', 'Failed to resume from history', err);
        setStatus('recordStatus', 'Failed to resume: ' + err.message, 'error');
    }
}

export async function uploadFromHistory(sessionId) {
    try {
        const session = await SessionStore.get(sessionId);
        if (!session || !session.segments || session.segments.length === 0) {
            setStatus('recordStatus', 'No audio data found in this recording.', 'error');
            loadRecordingHistory();
            return;
        }

        // CRITICAL: Stop any running timer/recording state first
        stopTimer();
        stopAutoSave();
        state.isRecording = false;
        state.isPaused = false;
        if (state.mediaRecorder && state.mediaRecorder.state !== 'inactive') {
            // Override onstop to prevent double-upload and null reference crash
            // (original onstop would call uploadAndProcess again since state.recordingInterrupted=false)
            const recorderRef = state.mediaRecorder;
            state.mediaRecorder.onstop = () => {
                try { recorderRef.stream.getTracks().forEach(t => t.stop()); } catch(e) {}
            };
            state.recordingInterrupted = false;
            try { state.mediaRecorder.stop(); } catch(e) {}
        }
        state.mediaRecorder = null;
        document.getElementById('recordBtn').classList.remove('recording');
        document.getElementById('recordBtn').style.display = '';
        hideRecordingControls();
        hideInterruptedUI();
        document.getElementById('timer').classList.remove('paused');
        document.getElementById('timer').textContent = '00:00:00';
        state.elapsedBeforePause = 0;
        state.startTime = null;
        state.startTimeMono = null; // FIX A1
        resetRecordingSegments(); // FIX A3

        // Validate segments are actual Blobs with content
        const validSegments = session.segments.filter(seg => {
            if (!seg) return false;
            if (typeof seg.size === 'number' && seg.size > 0) return true;
            if (seg.byteLength && seg.byteLength > 0) return true;
            return false;
        });

        if (validSegments.length === 0) {
            setStatus('recordStatus', 'Recording contains no valid audio data.', 'error');
            await SessionStore.remove(sessionId);
            loadRecordingHistory();
            return;
        }

        // FIX A3: keep the FULL mimeType including the codec parameter. Stripping
        // ';codecs=opus' is how a WebM/Opus stream ended up labelled as a bare
        // container (or worse, as audio/mp4) on the way to Transcribe.
        const uploadMimeType = (session.mimeType || '').trim() || 'audio/webm';
        const compositeBlob = new Blob(validSegments, { type: uploadMimeType });

        // SAFARI FIX: Consolidate multi-segment blob into single contiguous blob.
        // Safari/WebKit has a bug where Blobs from IndexedDB segments report correct .size
        // but fail when fetch()/XHR reads the bytes for upload.
        let finalBlob;
        try {
            const arrayBuffer = await compositeBlob.arrayBuffer();
            finalBlob = new Blob([arrayBuffer], { type: uploadMimeType });
            logger.info('UPLOAD', 'Blob consolidated for Safari compatibility', { 
                originalSegments: validSegments.length, 
                originalSize: compositeBlob.size, 
                consolidatedSize: finalBlob.size,
                mimeType: uploadMimeType 
            });
        } catch (consolidateErr) {
            logger.warn('UPLOAD', 'Blob consolidation failed, using composite blob', { error: consolidateErr.message });
            finalBlob = compositeBlob;
        }

        if (finalBlob.size === 0) {
            setStatus('recordStatus', 'Recording has no audio content. Removing.', 'error');
            await SessionStore.remove(sessionId);
            loadRecordingHistory();
            return;
        }

        logger.info('UPLOAD', 'Uploading from history', {
            sessionId,
            segments: validSegments.length,
            blobSize: finalBlob.size,
            mimeType: uploadMimeType,
            // FIX N1: patientLabel removed from this logger call (PHI)
        });

        state.currentSessionDbId = sessionId;
        state.currentSessionId = session.sessionId;
        document.getElementById('patientLabel').value = session.patientLabel || '';

        // Update session status to uploading and refresh history
        try { await SessionStore.update(sessionId, { status: 'uploading' }); } catch(e) {}
        loadRecordingHistory();

        // FIX A3: carry the audio metadata to /process so a failure is diagnosable
        await uploadAndProcess(finalBlob, 'record', {
            mimeType: uploadMimeType,
            segmentCount: validSegments.length,
            generationCount: 1, // stored session â€” provenance is a single recorded stream as far as we can tell
            source: 'history',
        });
    } catch (err) {
        logger.error('UPLOAD', 'Failed to upload from history', err);
        setStatus('recordStatus', 'Upload failed: ' + err.message, 'error');
    }
}

export async function discardFromHistory(sessionId) {
    var confirmed = await showConfirmDialog('Discard Recording', 'Discard this recording? This cannot be undone.', 'Discard', 'btn-danger');
    if (!confirmed) return;
    try {
        await SessionStore.remove(sessionId);
        if (state.currentSessionDbId === sessionId) {
            state.currentSessionDbId = null;
            state.currentSessionId = null;
            resetRecordingSegments(); // FIX A3
            state.elapsedBeforePause = 0;
        }
        loadRecordingHistory();
    } catch (err) {
        logger.warn('SYSTEM', 'Failed to discard session', { error: err.message });
    }
}

// v2.9.9: Download/Save audio from IndexedDB so doctor can upload from a different device
export async function downloadFromHistory(sessionId) {
    try {
        const session = await SessionStore.get(sessionId);
        if (!session || !session.segments || session.segments.length === 0) {
            showToast('No audio data found in this recording.', 'warning');
            return;
        }

        // Validate segments
        const validSegments = session.segments.filter(seg => seg && (typeof seg.size === 'number' ? seg.size > 0 : (seg.byteLength > 0)));
        if (validSegments.length === 0) {
            showToast('Recording contains no valid audio data.', 'warning');
            return;
        }

        const mimeType = session.mimeType || 'audio/webm';
        const audioBlob = new Blob(validSegments, { type: mimeType });

        if (audioBlob.size === 0) {
            showToast('Recording has no audio content.', 'warning');
            return;
        }

        // Determine file extension from mimeType
        let ext = 'webm';
        if (mimeType.includes('mp4') || mimeType.includes('m4a')) ext = 'm4a';
        else if (mimeType.includes('ogg')) ext = 'ogg';

        // Build filename: recording-{patientLabel}-{date}.{ext}
        const label = (session.patientLabel || 'visit').replace(/[^a-zA-Z0-9\-_]/g, '-').substring(0, 30);
        const dateStr = new Date(session.startedAt || Date.now()).toISOString().slice(0, 10);
        const filename = `recording-${label}-${dateStr}.${ext}`;

        // Try navigator.share with file support (iOS Safari, Android)
        if (navigator.canShare && navigator.canShare({ files: [new File([audioBlob], filename, { type: mimeType })] })) {
            try {
                await navigator.share({
                    files: [new File([audioBlob], filename, { type: mimeType })],
                    title: 'DentScribe Recording',
                    text: `Recording: ${session.patientLabel || 'visit'} (${(audioBlob.size / 1024 / 1024).toFixed(1)} MB)`,
                });
                logger.info('UPLOAD', 'Audio shared via navigator.share', { sessionId, size: audioBlob.size });
                return; // Share succeeded, no need to download
            } catch (shareErr) {
                // Share cancelled or failed â€” fall through to download
                if (shareErr.name !== 'AbortError') {
                    logger.warn('UPLOAD', 'navigator.share failed, falling back to download', { error: shareErr.message });
                }
            }
        }

        // Fallback: trigger browser download
        // iOS Safari doesn't support <a download> with blob URLs (WebKitBlobResource error 1)
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        if (isIOS) {
            // On iOS, open blob in new tab â€” Safari will show native media player / share option
            const url = URL.createObjectURL(audioBlob);
            const w = window.open(url, '_blank');
            if (!w) {
                // Popup blocked â€” instruct user
                showToast('To save audio: tap and hold the recording in Recent Recordings, then use "Share" from your browser menu.', 'info', 6000);
                URL.revokeObjectURL(url);
            } else {
                // Revoke after delay to keep the blob alive for the new tab
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            }
            logger.info('UPLOAD', 'Audio opened in new tab (iOS fallback)', { sessionId, filename, size: audioBlob.size });
        } else {
            const url = URL.createObjectURL(audioBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 1000);
            logger.info('UPLOAD', 'Audio downloaded from history', { sessionId, filename, size: audioBlob.size });
        }
    } catch (err) {
        logger.error('UPLOAD', 'Failed to download from history', err);
        showToast('Failed to save audio: ' + err.message, 'error');
    }
}

export function hideRecoveryBanner() {
    const banner = document.getElementById('recoveryBanner');
    if (banner) banner.classList.add('hidden');
}

// ============================================================================
// TIMER & WAKE LOCK
// ============================================================================
// FIX A1: idempotent — a second call can no longer orphan a running interval.
export function startTimer() {
    stopTimer();
    state.startTime = Date.now();          // absolute timestamp (kept for records)
    state.startTimeMono = monotonicNow();  // FIX A4: monotonic base for displayed elapsed

    // FIX 5: If we have an active session but state.elapsedBeforePause is zero/lost,
    // try to restore from IndexedDB (covers tab freeze / page visibility loss)
    if (state.currentSessionDbId && !state.elapsedBeforePause) {
        SessionStore.get(state.currentSessionDbId).then(session => {
            if (session && session.totalDurationMs > 0 && state.elapsedBeforePause < session.totalDurationMs) {
                state.elapsedBeforePause = session.totalDurationMs;
                logger.info('RECORDING', 'Timer restored elapsed from IndexedDB', { totalDurationMs: session.totalDurationMs });
                updateTimer(); // immediately show correct time
            }
        }).catch(() => {});
    }

    state.timerInterval = setInterval(updateTimer, 1000);
    updateTimer();
}

// FIX A1: null the handle so a later start can't double up.
export function stopTimer() {
    if (state.timerInterval) { clearInterval(state.timerInterval); state.timerInterval = null; }
}

// FIX A1/A4: guard the null base (was rendering ~494,000 h when state.startTime was
// null) and clamp/cap so a clock step can never produce a negative or absurd value.
export function updateTimer() {
    const el = document.getElementById('timer');
    if (!el) return;
    if (state.startTimeMono === null || state.startTimeMono === undefined) return; // no base â€” leave the DOM alone
    const e = totalElapsedMs();
    let hours = Math.floor(e / 3600000);
    if (hours > MAX_DISPLAY_HOURS) hours = MAX_DISPLAY_HOURS;
    el.textContent =
        pad(hours) + ':' + pad(Math.floor((e % 3600000) / 60000)) + ':' + pad(Math.floor((e % 60000) / 1000));
}

export async function requestWakeLock() {
    try {
        if ('wakeLock' in navigator) {
            state.wakeLock = await navigator.wakeLock.request('screen');
            logger.info('SYSTEM', 'Wake lock acquired');
            document.getElementById('wakeLockNotice').style.display = 'block';
        }
    } catch (err) { logger.warn('SYSTEM', 'Wake Lock not available', { error: err.message }); }
}

export async function releaseWakeLock() {
    if (state.wakeLock) { await state.wakeLock.release(); state.wakeLock = null; document.getElementById('wakeLockNotice').style.display = 'none'; }
}

// ============================================================================
// INPUT VALIDATION HELPERS
// ============================================================================
